/**
 * The chart contract: what to draw, said without naming a drawing library.
 *
 * THIS FILE IMPORTS NO CHART LIBRARY, and neither does anything that builds a
 * {@link ChartSpec}. That is the whole point. A charting dependency is a
 * long-lived, load-bearing choice made under short-lived information — the
 * library that is stable today is the one shipping breaking changes between
 * minor versions next year — so the choice is confined to a single adapter
 * module and everything else talks in this vocabulary instead.
 *
 * The translation below is where the pivot's meaning is decided (what a null
 * is, which axis is series, what a slice is). An adapter receives that decided
 * meaning and does nothing but draw it, so swapping adapters cannot change a
 * number, only its pixels — and the tests for this file keep holding across
 * the swap, because they never touch a renderer.
 *
 * ROWS ARE CATEGORIES, COLUMNS ARE SERIES. The same two axes the pivot table
 * uses, read differently: a table nests them, a chart puts one along the
 * bottom and gives the other its own colour.
 */
import type {
  DimTuple,
  PivotView,
  ReportModel,
  ReportSpec,
  ResolvedFormat,
  VizKind,
} from '@clockwise/semantic-core';
import {
  dimText,
  findDimension,
  findMeasure,
  resolveValueFormat,
  tupleKey,
} from '@clockwise/semantic-core';
import { formatCompactResolved, formatResolved } from '../format';

/** The chart shapes an adapter must draw. `table` and `metric` are not charts. */
export type ChartKind = Exclude<VizKind, 'table' | 'metric'>;

/** One position along the bottom (or one slice of a pie). */
export interface ChartCategory {
  /** Stable identity. Two categories may share a label; they never share this. */
  key: string;
  label: string;
  /** The row tuple this category IS — what a click on it pins. */
  rowKey?: DimTuple;
}

export interface ChartSeries {
  key: string;
  label: string;
  /**
   * One entry per category, in the same order — `points[i]` belongs to
   * `categories[i]`.
   *
   * `null` IS NOT ZERO, and an adapter must not substitute one. A missing
   * month plotted at zero reads as "they billed nothing" rather than "we have
   * no number for this", and the dive to the axis is the loudest thing on the
   * chart. A line breaks across a null; a bar omits the segment entirely.
   */
  points: (number | null)[];
  /**
   * This series' values as the reader should read them, where it differs from
   * {@link ChartSpec.format}.
   *
   * Two measures can share an axis unit and still not share a format — hours
   * to one decimal beside hours to two, a currency scaled to thousands beside
   * one read whole — and a tooltip has to read exactly as the table cell
   * behind it. Absent means the chart's own format applies.
   */
  format?: (v: number | null) => string;
  /** The column tuple this series draws — absent when there is no Columns axis. */
  colKey?: DimTuple;
  /** The measure this series draws. */
  measureId?: string;
}

/**
 * Where a click landed, as indices into {@link ChartSpec}.
 *
 * `series` is OPTIONAL because some marks genuinely cannot report one. A click
 * on a bar segment or a pie slice identifies both coordinates; a click in the
 * plot area of a multi-line chart identifies an x position and nothing more —
 * there is no line under the cursor, only a column of them.
 *
 * That absence is meaningful rather than a gap to paper over. A coordinate
 * with no series is the same thing a row-total cell is in the pivot: the
 * category axis is constrained and the series axis is not, which is already a
 * defined drilldown case. Reporting series 0 instead would be a guess, and a
 * drilldown built on a guess filters by a customer the reader never clicked.
 */
export interface ChartPoint {
  category: number;
  series?: number;
}

export interface ChartSpec {
  kind: ChartKind;
  /** Bands add up to the category's total rather than sitting side by side. */
  stacked: boolean;
  categories: ChartCategory[];
  series: ChartSeries[];
  /** What the numbers are, e.g. "Client Billable Hours". For tooltips. */
  measureLabel: string;
  /** What the bottom axis is, e.g. "Month". Absent when there are no rows. */
  categoryLabel?: string;
  /** What the colours are, e.g. "Customer". Absent when nothing is on Columns. */
  seriesLabel?: string;
  /** A value as the reader should read it — units, decimals, percent sign. */
  format: (v: number | null) => string;
  /** The same, shortened for axis ticks where space is scarce. */
  formatTick: (v: number) => string;
  /**
   * Draw the shape ALONE — no axes, grid, legend or dots; only a one-line
   * hover naming the point's period and value.
   *
   * For a sparkline under a metric: the number above it carries the value and
   * the delta beside it carries the direction, so axes would repeat what has
   * already been said in a space too small to read them. The series is there
   * to show shape, not to be measured off — the hover is for the reader who
   * wants one point's number without opening the report.
   *
   * A flag on the spec rather than a separate adapter entry point, so a
   * sparkline goes through exactly the same renderer as a full chart and
   * cannot drift from it.
   */
  compact?: boolean;
  /**
   * Run the bars ACROSS, with the categories down the side.
   *
   * A vertical bar gets one label slot as wide as the bar itself, and the
   * categories on this model are `Customer · Role` — thirty characters where
   * twenty pixels are on offer. Recharts' answer is to drop whatever will not
   * fit, silently, so the reader is left with named bars next to anonymous
   * ones and no way to tell which names went missing. Turned on its side, a
   * label gets a whole row and every category keeps its name.
   *
   * Decided in {@link pivotToChartSpec} rather than saved on the spec, because
   * it is a consequence of what is on the row axis rather than a preference —
   * see {@link runsAcross}.
   */
  horizontal?: boolean;
  /**
   * Drilldown seam. Absent means the marks are inert, which is what a placed
   * dashboard tile wants until drilldown ships.
   */
  onPointClick?: (at: ChartPoint) => void;
  /**
   * Measures on Values that this chart does NOT draw, by label.
   *
   * A chart has one value axis, so a measure that reads in a different unit
   * from the first — currency beside hours — cannot share it. It is left out
   * rather than plotted against a scale that is not its own, and it is named
   * here so the caller can SAY it was left out: a withheld series and a
   * missing one render identically, and only one of them is fixed by moving
   * the measure to a table. Absent or empty when every measure is drawn.
   */
  omittedMeasures?: readonly string[];
}

/** A tuple's display text: labels, never keys. */
const text = (t: DimTuple) => t.map(dimText).join(' · ');

/**
 * The single series a chart with nothing on Columns still has.
 *
 * A bar of one measure grouped only by row has one band per category, and
 * that band is a series of one — representing it as zero series would make
 * every adapter write the same "if empty, invent one" branch.
 */
const IMPLICIT_SERIES_KEY = '';

/**
 * Pivot → chart, deciding the meaning once.
 *
 * Returns `null` when there is nothing to draw — no measure selected, or an
 * empty result. A caller renders its own empty state rather than an adapter
 * inventing one, so the message matches every other empty state on the page.
 */
export function pivotToChartSpec(
  model: ReportModel,
  spec: ReportSpec,
  view: PivotView,
  onPointClick?: (at: ChartPoint) => void,
  /** Draw this as something other than the spec's own `viz` — see `compact`. */
  as?: { kind: ChartKind; compact?: boolean },
): ChartSpec | null {
  const kind = as?.kind ?? spec.viz;
  if (kind === 'table' || kind === 'metric') return null;

  // The FIRST measure sets the axis: its unit, its tick format, its label.
  const measureId = spec.measures[0];
  if (!measureId) return null;
  const measure = findMeasure(model, measureId);
  // How this measure READS — the one resolution the tooltip and the axis share.
  // It carries the `showAs` mode too, for anything that needs to tell a share
  // from a quantity.
  const resolved = chartFormat(resolveValueFormat(measure, spec, measureId));

  if (view.rowKeys.length === 0) return null;

  // EVERY MEASURE THAT CAN SHARE THAT AXIS, not the first alone. This used to
  // draw `measures[0]` and nothing else, so a bar of Billable and Non-Billable
  // hours showed Billable only — no message, while the Values shelf beside it
  // listed both. The second measure was the one the author added on purpose.
  //
  // "Can share" is the UNIT after `showAs`, because that is what the axis is
  // measured in: two hour measures plot against one scale; hours beside
  // currency would put one of them against numbers that are not its own. Those
  // are left out and NAMED in `omittedMeasures`, so the caller can say so.
  //
  // A pie keeps the first measure alone: its slices are the categories, so it
  // has exactly one series to draw, and the guards refuse a second measure on
  // one anyway.
  const drawn: { id: string; label: string; format: ResolvedFormat }[] = [];
  const omittedMeasures: string[] = [];
  for (const id of spec.measures) {
    const m = findMeasure(model, id);
    const format = chartFormat(resolveValueFormat(m, spec, id));
    const label = m?.label ?? id;
    const fits = format.format === resolved.format && (kind !== 'pie' || drawn.length === 0);
    if (fits) drawn.push({ id, label, format });
    else omittedMeasures.push(label);
  }
  const several = drawn.length > 1;

  const categories: ChartCategory[] = view.rowKeys.map((rk) => ({
    key: tupleKey(rk),
    label: text(rk),
    rowKey: rk,
  }));

  // A pie has no series axis — the guards refuse one — so its slices are the
  // categories and the values ride on a single unnamed series. Same shape as
  // every other chart, so an adapter reads `series[s].points[c]` uniformly.
  const colKeys: DimTuple[] = view.colKeys.length > 0 ? [...view.colKeys] : [[]];

  // COLUMN OUTSIDE, MEASURE INSIDE — the order the pivot table lays them out
  // in, so the legend reads in the same sequence as the header above the
  // table. The measure only enters the key and label when there is more than
  // one, so a single-measure chart keeps exactly the series it always had.
  const series: ChartSeries[] = colKeys.flatMap((ck) =>
    drawn.map((d) => {
      const col = ck.length > 0 ? tupleKey(ck) : IMPLICIT_SERIES_KEY;
      return {
        key: several ? `${col}\u0000${d.id}` : col,
        label: ck.length === 0 ? d.label : several ? `${text(ck)} · ${d.label}` : text(ck),
        points: view.rowKeys.map((rk) => view.cell(rk, ck, d.id)),
        format: (v: number | null) => formatResolved(v, d.format),
        ...(ck.length > 0 ? { colKey: ck } : {}),
        measureId: d.id,
      };
    }),
  );

  return {
    kind,
    compact: as?.compact,
    horizontal: kind === 'bar' && as?.compact !== true && runsAcross(model, spec.rows),
    // Asked of the spec, not inferred from the series count. One series that
    // happens to be alone is still an unstacked chart, and inferring would
    // make the same report stack or not depending on its data.
    stacked: spec.stacked,
    categories,
    series,
    measureLabel: measure?.label ?? measureId,
    categoryLabel: axisLabel(model, spec.rows),
    seriesLabel: axisLabel(model, spec.cols),
    // The SAME resolution the table cells use, so a tooltip reads exactly as
    // the cell behind it does — brackets included. Not the colour: a tooltip
    // formatter returns a string, and a string cannot carry one.
    //
    // Resolved ONCE here rather than inside the closures, which are called per
    // point and per tick.
    format: (v) => formatResolved(v, resolved),
    // The axis reads in the SAME unit as the tooltip above it — including the
    // symbol, the separators and the sign style the author chose. Bound to the
    // measure's declared format instead, an axis kept a `$` the author had
    // just removed.
    formatTick: (v) => formatCompactResolved(v, resolved),
    onPointClick,
    omittedMeasures,
  };
}

/**
 * A measure's format as a chart can carry it.
 *
 * COLOUR IS NOT AVAILABLE ON THIS SURFACE. `redOnly` is Excel's red negative
 * with the minus dropped, and it is only legible because a table cell takes
 * `.rk-neg` alongside it — a tooltip and an axis tick are plain strings that no
 * class is ever applied to. Left as chosen, a chart drew -2,400,000 as "$2.4M",
 * character for character the same as +2,400,000, so a credit read as a gain.
 * Falling back to the minus keeps the shape cue, which is the same reasoning
 * that kept `minusRed` in the vocabulary beside it.
 */
function chartFormat(format: ResolvedFormat): ResolvedFormat {
  return format.negative === 'redOnly' ? { ...format, negative: 'minus' } : format;
}

/**
 * Whether a bar chart over these row dimensions should lie on its side.
 *
 * TIME IS THE EXCEPTION, not the rule. Months read left to right — that
 * direction is what makes a run of bars a trend rather than a ranking — and
 * a period label is short enough to fit under its own bar, so a time axis
 * stays upright. Everything else on these models is a name: a customer, a
 * role, an activity, usually two of them joined. Names are long, there are
 * many of them, and the reader is comparing magnitudes rather than following
 * a sequence, which is the horizontal bar's case exactly.
 *
 * Asked of the model's `isTime` flag rather than of the dimension's id, the
 * same way {@link PivotTable} decides which columns may not wrap: the models
 * carry more than one time dimension and Budget Period is not called `month`.
 *
 * A chart with NO row dimensions cannot be drawn at all (`pivotToChartSpec`
 * has already returned by then), so the empty case never reaches here; it
 * answers `false` rather than throwing because a default orientation is not
 * worth an exception.
 */
function runsAcross(model: ReportModel, rows: readonly string[]): boolean {
  if (rows.length === 0) return false;
  return !rows.some((id) => findDimension(model, id)?.isTime === true);
}

/**
 * What to call an axis carrying these dimensions.
 *
 * Nested dimensions are joined the same way their VALUES are, so the axis
 * title and the tick beneath it read as the same two-part thing — "Customer ·
 * Activity" over "Acme · Advisory".
 */
function axisLabel(model: ReportModel, ids: readonly string[]): string | undefined {
  if (ids.length === 0) return undefined;
  return ids.map((id) => findDimension(model, id)?.label ?? id).join(' · ');
}

/**
 * Every category that has a number in at least one series.
 *
 * Not every adapter needs this, but the ones that draw an axis do: a category
 * that is null across the board still occupies a tick, and a run of them at
 * the end of a period (months that have not happened yet) makes the drawn part
 * of the chart shrink into the corner.
 */
export function populatedCategories(spec: ChartSpec): number[] {
  return spec.categories
    .map((_, i) => i)
    .filter((i) => spec.series.some((s) => s.points[i] !== null && s.points[i] !== undefined));
}
